import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Modal } from '../components/Modal';
import { useEvent } from '../hooks/useEvent';
import { useSync } from '../hooks/useSync';
import { getNickname, setNickname } from '../lib/db';

export function EventPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const {
    event,
    proposals,
    publishedVersion,
    isLoading,
    error,
    isOrganizer,
    hasAccessCode,
    deviceId,
    nickname: currentNickname,
    verifyAccess,
    loginOrganizer,
  } = useEvent(eventId ?? null);
  const { isSyncing, lastSyncAt } = useSync(eventId ?? null);
  
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-8 h-8 border-4 border-primary-200 border-t-primary-400 rounded-full animate-spin" />
            <p className="mt-2 text-gray-500">Loading event...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !event) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-red-50 text-red-700 p-4 rounded-lg">
            {error || 'Event not found'}
          </div>
          <Link to="/" className="btn btn-secondary mt-4">
            Go to Session Board
          </Link>
        </div>
      </Layout>
    );
  }

  const formattedDate = new Date(event.startDate).toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Layout title={event.name} eventId={eventId} isOrganizer={isOrganizer} nickname={currentNickname} deviceId={deviceId}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Welcome + Event header */}
        <div className="mb-8">
          {currentNickname && (
            <p className="text-lg text-gray-500 mb-1">
              Welcome, <span className="font-semibold text-gray-800">{currentNickname}</span>!
            </p>
          )}
          <h1 className="text-3xl font-bold text-gray-900">{event.name}</h1>
          <p className="text-lg text-gray-600 mt-2">{formattedDate}</p>
          
          {/* Sync status */}
          {lastSyncAt && (
            <p className="text-sm text-gray-400 mt-1">
              {isSyncing ? 'Syncing...' : `Last synced ${new Date(lastSyncAt).toLocaleTimeString()}`}
            </p>
          )}

          {/* Access badges */}
          <div className="flex gap-2 mt-4">
            {hasAccessCode && (
              <span className="badge badge-green">Access Granted</span>
            )}
            {isOrganizer && (
              <span className="badge badge-primary">Organizer</span>
            )}
            {event.archivedAt && (
              <span className="badge badge-gray">Archived</span>
            )}
            {event.presentingProposalId && (
              <span className="badge badge-yellow">Presentation in progress</span>
            )}
          </div>
        </div>

        {/* Submit session card CTA */}
        {(hasAccessCode || isOrganizer) && (
          <div className="mb-8">
            <Link
              to={`/event/${eventId}/proposals?create=true`}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Submit Session Card
            </Link>
          </div>
        )}

        {/* Quick actions */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-8">
          {/* Board: visible to organizers always, attendees only if published */}
          {(isOrganizer || publishedVersion) && (
            <QuickActionCard
              title="View Board"
              description="See the session schedule"
              to={`/event/${eventId}/board`}
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              }
            />
          )}
          
          <QuickActionCard
            title="Session Cards"
            description={`${proposals.length} session cards`}
            to={`/event/${eventId}/proposals`}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />

          {isOrganizer && (
            <QuickActionCard
              title="Admin Panel"
              description="Manage rooms, slots, and schedule"
              to={`/event/${eventId}/admin`}
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              }
            />
          )}
        </div>

        {/* Access controls */}
        {!hasAccessCode && !isOrganizer && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
            <h3 className="font-medium text-yellow-800">Enter Access Code</h3>
            <p className="text-sm text-yellow-700 mt-1">
              You need the event access code to submit session cards and show interest.
            </p>
            <button
              onClick={() => setShowAccessModal(true)}
              className="btn btn-secondary btn-sm mt-3"
            >
              Enter Access Code
            </button>
          </div>
        )}

        {!isOrganizer && (
          <div className="text-center mt-4">
            <button
              onClick={() => setShowLoginModal(true)}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Organizer Login
            </button>
          </div>
        )}

        {/* Published version info */}
        {publishedVersion && (
          <div className="rounded-lg p-4 mt-8" style={{ background: '#FFFDF5', border: '1px solid rgba(0,0,0,0.06)' }}>
            <h3 className="font-medium text-gray-900">Published Schedule</h3>
            <p className="text-sm text-gray-500 mt-1">
              Version {publishedVersion.versionNum} - Published{' '}
              {new Date(publishedVersion.publishedAt).toLocaleString()}
            </p>
          </div>
        )}

        {/* Access Code Modal */}
        <AccessCodeModal
          isOpen={showAccessModal}
          onClose={() => setShowAccessModal(false)}
          onVerify={verifyAccess}
        />

        {/* Organizer Login Modal */}
        <OrganizerLoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLogin={loginOrganizer}
        />

      </div>
    </Layout>
  );
}

function QuickActionCard({
  title,
  description,
  to,
  icon,
}: {
  title: string;
  description: string;
  to: string;
  icon: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className="card card-hover flex items-start gap-4"
    >
      <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(247,194,61,0.15)', color: '#C79A00' }}>
        {icon}
      </div>
      <div>
        <h3 className="font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </Link>
  );
}

function AccessCodeModal({
  isOpen,
  onClose,
  onVerify,
}: {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (code: string) => Promise<boolean>;
}) {
  const [code, setCode] = useState('');
  const [nickname, setNicknameInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-fill nickname if already set
  React.useEffect(() => {
    if (isOpen) {
      getNickname().then((n) => { if (n) setNicknameInput(n); });
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nickname.trim()) {
      setError('Please enter your name');
      return;
    }
    setIsSubmitting(true);
    setError(null);

    const success = await onVerify(code);
    if (success) {
      await setNickname(nickname.trim());
      onClose();
    } else {
      setError('Invalid access code');
    }
    setIsSubmitting(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Join Event"
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="submit"
            form="access-code-form"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Joining...' : 'Join Event'}
          </button>
        </>
      }
    >
      <form id="access-code-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        <div>
          <label htmlFor="nickname" className="label">
            Your Name *
          </label>
          <input
            id="nickname"
            type="text"
            className="input"
            value={nickname}
            onChange={(e) => setNicknameInput(e.target.value)}
            placeholder="How should we call you?"
            autoFocus
            required
          />
          <p className="text-xs text-gray-500 mt-1">This will be your default name on session cards.</p>
        </div>
        <div>
          <label htmlFor="accessCode" className="label">
            Access Code *
          </label>
          <input
            id="accessCode"
            type="text"
            className="input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter the event access code"
            required
          />
        </div>
      </form>
    </Modal>
  );
}

function OrganizerLoginModal({
  isOpen,
  onClose,
  onLogin,
}: {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (password: string) => Promise<boolean>;
}) {
  const [password, setPassword] = useState('');
  const [nickname, setNicknameInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pre-fill nickname if already set
  React.useEffect(() => {
    if (isOpen) {
      getNickname().then((n) => { if (n) setNicknameInput(n); });
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nickname.trim()) {
      setError('Please enter your name');
      return;
    }
    setIsSubmitting(true);
    setError(null);

    const success = await onLogin(password);
    if (success) {
      await setNickname(nickname.trim());
      onClose();
    } else {
      setError('Invalid password');
    }
    setIsSubmitting(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Organizer Login"
      footer={
        <>
          <button onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="submit"
            form="organizer-login-form"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Logging in...' : 'Login'}
          </button>
        </>
      }
    >
      <form id="organizer-login-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}
        <div>
          <label htmlFor="organizer-name" className="label">
            Your Name *
          </label>
          <input
            id="organizer-name"
            type="text"
            className="input"
            value={nickname}
            onChange={(e) => setNicknameInput(e.target.value)}
            placeholder="How should we call you?"
            autoFocus
            required
          />
        </div>
        <div>
          <label htmlFor="password" className="label">
            Organizer Password *
          </label>
          <input
            id="password"
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
      </form>
    </Modal>
  );
}
